import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useReactiveVar } from '@apollo/client';
import getFromUserSettings from '/imports/ui/services/users-settings';
import Auth from '/imports/ui/services/auth';
import ActionsBar from './component';
import {
  layoutSelectOutput,
  layoutSelectInput,
  layoutDispatch,
  layoutSelect,
} from '../layout/context';
import {
  useIsExternalVideoEnabled,
  useIsPollingEnabled,
  useIsPresentationEnabled,
  useIsTimerFeatureEnabled,
} from '/imports/ui/services/features';
import useHasUnreadNotes from '../notes/hooks/useHasUnreadNotes';
import { PluginsContext } from '/imports/ui/components/components-data/plugin-context/context';
import {
  CURRENT_PRESENTATION_PAGE_SUBSCRIPTION,
} from '/imports/ui/components/whiteboard/queries';
import MediaService from '../media/service';
import useMeeting from '/imports/ui/core/hooks/useMeeting';
import useCurrentUser from '/imports/ui/core/hooks/useCurrentUser';
import { EXTERNAL_VIDEO_STOP } from '../external-video-player/mutations';
import { PINNED_PAD_SUBSCRIPTION } from '../notes/queries';
import useDeduplicatedSubscription from '../../core/hooks/useDeduplicatedSubscription';
import connectionStatus from '../../core/graphql/singletons/connectionStatus';
import { useMeetingLayoutUpdater, usePushLayoutUpdater } from '../layout/push-layout/hooks';
import useSettings from '/imports/ui/services/settings/hooks/useSettings';
import { SETTINGS } from '/imports/ui/services/settings/enums';
import deviceInfo from '/imports/utils/deviceInfo';
import { SMALL_VIEWPORT_BREAKPOINT } from '../layout/enums';
import { useShortcut } from '../../core/hooks/useShortcut';
import useChat from '/imports/ui/core/hooks/useChat';
import { courseSelect } from '../app/context';

const isLayeredView = window.matchMedia(`(max-width: ${SMALL_VIEWPORT_BREAKPOINT}px)`);

const isReactionsButtonEnabled = () => {
  const USER_REACTIONS_ENABLED = window.meetingClientSettings.public.userReaction.enabled;
  const REACTIONS_BUTTON_ENABLED = window.meetingClientSettings.public.app.reactionsButton.enabled;

  return USER_REACTIONS_ENABLED && REACTIONS_BUTTON_ENABLED;
};



const ActionsBarContainer = (props) => {
  const NOTES_CONFIG = window.meetingClientSettings.public.notes;
  const LAYOUT_CONFIG = window.meetingClientSettings.public.layout;
  const { showPushLayoutButton } = LAYOUT_CONFIG;
  const actionsBarStyle = layoutSelectOutput((i) => i.actionBar);
  const layoutContextDispatch = layoutDispatch();
  const cameraDockOutput = layoutSelectOutput((i) => i.cameraDock);
  const cameraDockInput = layoutSelectInput((i) => i.cameraDock);
  const presentationInput = layoutSelectInput((i) => i.presentation);
  const sidebarNavigation = layoutSelectInput((i) => i.sidebarNavigation);
  // const sidebarContent = layoutSelectInput((i) => i.sidebarContent);

  const { width: browserWidth } = layoutSelectInput((i) => i.browser);
  const isMobile = browserWidth <= SMALL_VIEWPORT_BREAKPOINT;


  const { data: presentationPageData } = useDeduplicatedSubscription(
    CURRENT_PRESENTATION_PAGE_SUBSCRIPTION,
  );
  const presentationPage = presentationPageData?.pres_page_curr[0] || {};
  const isThereCurrentPresentation = !!presentationPage?.presentationId;

  const genericMainContent = layoutSelectInput((i) => i.genericMainContent);
  const isThereGenericMainContent = !!genericMainContent.genericContentId;

  const { data: currentMeeting } = useMeeting((m) => ({
    externalVideo: m.externalVideo,
    componentsFlags: m.componentsFlags,
  }));

  const isSharingVideo = !!currentMeeting?.externalVideo?.externalVideoUrl;

  const {
    pluginsExtensibleAreasAggregatedState,
  } = useContext(PluginsContext);
  let actionBarItems = [];
  if (pluginsExtensibleAreasAggregatedState.actionsBarItems) {
    actionBarItems = [
      ...pluginsExtensibleAreasAggregatedState.actionsBarItems,
    ];
  }

  const { data: currentUserData } = useCurrentUser((user) => ({
    presenter: user.presenter,
    isModerator: user.isModerator,
  }));

  const [stopExternalVideoShare] = useMutation(EXTERNAL_VIDEO_STOP);

  const { data: chats } = useChat((chat) => ({
    totalUnread: chat.totalUnread,
    chatId: chat.chatId,
    public: chat.public,
    totalUnread: chat.totalUnread,
    participant: chat.participant, // keep this if you want private user info
  }));



  // const { data: allChat } = useChat((chat) => ({
  //   chatId: chat.chatId,
  //   public: chat.public,
  //   totalUnread: chat.totalUnread,
  //   participant: chat.participant, // keep this if you want private user info
  // }));

  const hasUnreadPublic = chats?.some(c => c.public && c.totalUnread > 0);
  const hasUnreadPrivate = chats?.some(c => !c.public && c.totalUnread > 0);
  const courseDetails = courseSelect((state) => state);
  const isPrepareLesson = courseDetails?.course?.courseDetail?.isPrepareLesson ?? false

  const toggleUserList = useShortcut('toggleUserList');
  const currentUser = {
    userId: Auth.userID,
  };
  const amIPresenter = currentUserData?.presenter;
  const amIModerator = currentUserData?.isModerator;
  const [pinnedPadDataState, setPinnedPadDataState] = useState(null);
  const { data: pinnedPadData } = useDeduplicatedSubscription(
    PINNED_PAD_SUBSCRIPTION,
  );

  const allowExternalVideo = useIsExternalVideoEnabled();
  const connected = useReactiveVar(connectionStatus.getConnectedStatusVar());
  const intl = useIntl();
  const isPresentationEnabled = useIsPresentationEnabled();
  const isTimerFeatureEnabled = useIsTimerFeatureEnabled();
  const isPollingEnabled = useIsPollingEnabled() && isPresentationEnabled;
  const applicationSettings = useSettings(SETTINGS.APPLICATION);
  const { pushLayout } = applicationSettings;
  const setPushLayout = usePushLayoutUpdater(pushLayout);
  const setMeetingLayout = useMeetingLayoutUpdater(
    cameraDockOutput,
    cameraDockInput,
    presentationInput,
    applicationSettings,
  );
  const sidebarContent = layoutSelectInput((i) => i.sidebarContent);
  const unread = useHasUnreadNotes();
  const sharedNotes = layoutSelectInput((i) => i.sharedNotes);
  const idChatOpen = layoutSelect((i) => i.idChatOpen);
  const { data: chatData } = useChat((chat) => chat)
  const { isOpen: sidebarNavigationIsOpen } = sidebarNavigation;
  const { isOpen: sidebarContentIsOpen } = sidebarContent;
  const ariaHidden = sidebarNavigationIsOpen
    && sidebarContentIsOpen
    && (deviceInfo.isPhone || isLayeredView.matches);
  console.log('[ActionsBar GATES]', {
    display: actionsBarStyle.display,
    hasCurrentMeeting: !!currentMeeting,
    hasPinnedPadData: !!pinnedPadData,
    isPrepareLesson,
    courseDetails: JSON.stringify(courseDetails),
  });
  if (actionsBarStyle.display === false) {
    console.log('[ActionsBar] BLOCKED: display === false');
    return null;
  }
  if (!currentMeeting) {
    console.log('[ActionsBar] BLOCKED: no currentMeeting (useMeeting returned null)');
    return null;
  }
  if (!pinnedPadData) {
    console.log('[ActionsBar] BLOCKED: no pinnedPadData');
    return null;
  }

  const isSharedNotesPinnedFromGraphql = !!pinnedPadData
    && pinnedPadData.sharedNotes[0]?.sharedNotesExtId === NOTES_CONFIG.id;

  const isSharedNotesPinned = isSharedNotesPinnedFromGraphql;

  const hasUnreadMessages = chats && chats.reduce((acc, chat) => acc + chat?.totalUnread, 0) > 0;
  const { isPinned: notesIsPinned } = sharedNotes;
  // Get course context to check isPrepareLesson


  return (
    <ActionsBar {
      ...{
        ...props,
        enableVideo: getFromUserSettings('bbb_enable_video', window.meetingClientSettings.public.kurento.enableVideo),
        // showScreenshareQuickSwapButton: window.meetingClientSettings
        //   .public.layout.showScreenshareQuickSwapButton,
        showScreenshareQuickSwapButton: true,
        multiUserTools: getFromUserSettings('bbb_multi_user_tools', window.meetingClientSettings.public.whiteboard.toolbar.multiUserTools),
        isReactionsButtonEnabled: isReactionsButtonEnabled(),
        setPresentationIsOpen: MediaService.setPresentationIsOpen,
        hasScreenshare: currentMeeting?.componentsFlags?.hasScreenshare ?? false,
        isMeteorConnected: connected,
        hasCameraAsContent: currentMeeting?.componentsFlags?.hasCameraAsContent,
        intl,
        allowExternalVideo,
        isPollingEnabled,
        isPresentationEnabled,
        currentUser,
        amIModerator,
        layoutContextDispatch,
        actionsBarStyle,
        amIPresenter,
        actionBarItems,
        isThereCurrentPresentation,
        isSharingVideo,
        stopExternalVideoShare,
        isSharedNotesPinned,
        isTimerActive: currentMeeting.componentsFlags.hasTimer,
        isTimerEnabled: isTimerFeatureEnabled,
        hasGenericContent: isThereGenericMainContent,
        setPushLayout,
        setMeetingLayout,
        showPushLayout: showPushLayoutButton && applicationSettings.selectedLayout === 'custom',
        ariaHidden,
        isMobile,
        sidebarNavigation,
        shortcuts: toggleUserList,
        hasUnreadMessages,
        sidebarContent,
        unread,
        notesIsPinned,
        sidebarContentIsOpen,
        idChatOpen,
        hasUnreadPublic,
        hasUnreadPrivate,
        isPrepareLesson
      }
    }
    />
  );
};

export default ActionsBarContainer;
